import React, { useState, useCallback } from 'react';
import type { AxiosInstance } from 'axios';
import { GenericTable } from '@ui-components/Table/GenericTable';
import GenericButton from '@ui-components/Button/GenericButton';
import { GenericTag } from '@ui-components/Tag/GenericTag';
import AlertRecipientFormModal from './AlertRecipientFormModal';
import type { FortiFlexAlertRecipient } from '../hooks/useFortiFlexData';
import {
    createAlertRecipient,
    updateAlertRecipient,
    deleteAlertRecipient,
} from '../request/request';

interface AlertRecipientsTableProps {
    recipients: FortiFlexAlertRecipient[];
    api: AxiosInstance;
    onMutate: () => void;
}

const AlertRecipientsTable: React.FC<AlertRecipientsTableProps> = ({
    recipients,
    api,
    onMutate,
}) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [editItem, setEditItem] = useState<FortiFlexAlertRecipient | null>(null);
    const [saving, setSaving] = useState(false);
    const [busyId, setBusyId] = useState<number | null>(null);

    const openCreate = () => {
        setEditItem(null);
        setModalOpen(true);
    };

    const openEdit = (item: FortiFlexAlertRecipient) => {
        setEditItem(item);
        setModalOpen(true);
    };

    const handleSubmit = useCallback(
        async (data: { email: string; name: string; enabled: boolean }) => {
            setSaving(true);
            try {
                if (editItem) {
                    await updateAlertRecipient(api, editItem.id, data);
                } else {
                    await createAlertRecipient(api, data);
                }
                setModalOpen(false);
                onMutate();
            } catch {
                // error visible via network
            } finally {
                setSaving(false);
            }
        },
        [api, editItem, onMutate]
    );

    const handleDelete = useCallback(
        async (id: number) => {
            setBusyId(id);
            try {
                await deleteAlertRecipient(api, id);
                onMutate();
            } catch {
                // error visible via network
            } finally {
                setBusyId(null);
            }
        },
        [api, onMutate]
    );

    const handleToggle = useCallback(
        async (item: FortiFlexAlertRecipient) => {
            setBusyId(item.id);
            try {
                await updateAlertRecipient(api, item.id, { enabled: !item.enabled });
                onMutate();
            } catch {
                // error visible via network
            } finally {
                setBusyId(null);
            }
        },
        [api, onMutate]
    );

    const columns = [
        {
            header: 'Email',
            accessor: 'email' as keyof FortiFlexAlertRecipient,
            width: '250px',
        },
        {
            header: 'Name',
            width: '200px',
            renderCell: (r: FortiFlexAlertRecipient) => (
                <span className="text-semantic-text-primary">{r.name || '-'}</span>
            ),
        },
        {
            header: 'Status',
            width: '100px',
            renderCell: (r: FortiFlexAlertRecipient) => (
                <GenericTag
                    label={r.enabled ? 'Enabled' : 'Disabled'}
                    variant={r.enabled ? 'success' : 'neutral'}
                    size="small"
                    rounded="full"
                />
            ),
        },
        {
            header: 'Actions',
            width: '220px',
            renderCell: (r: FortiFlexAlertRecipient) => {
                const busy = busyId === r.id;
                return (
                    <div className="flex items-center gap-2">
                        <GenericButton
                            variant="secondary"
                            size="sm"
                            label={r.enabled ? 'Disable' : 'Enable'}
                            onClick={() => handleToggle(r)}
                            loading={busy}
                            disabled={busy}
                        />
                        <GenericButton
                            variant="secondary"
                            size="sm"
                            label="Edit"
                            onClick={() => openEdit(r)}
                            disabled={busy}
                        />
                        <GenericButton
                            variant="danger"
                            size="sm"
                            label="Delete"
                            onClick={() => handleDelete(r.id)}
                            loading={busy}
                            disabled={busy}
                        />
                    </div>
                );
            },
        },
    ];

    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <div>
                    <h3 className="text-sm font-semibold text-semantic-text-primary">
                        Alert Recipients
                    </h3>
                    <p className="text-xs text-semantic-text-secondary mt-0.5">
                        WARNING alerts: Monday and Friday | CRITICAL alerts: every day
                    </p>
                </div>
                <GenericButton
                    variant="brand"
                    size="md"
                    label="Add Recipient"
                    onClick={openCreate}
                />
            </div>
            <GenericTable
                columns={columns}
                data={recipients}
                initialRowsPerPage={10}
            />
            <AlertRecipientFormModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                onSubmit={handleSubmit}
                recipient={editItem}
                loading={saving}
            />
        </div>
    );
};

export default AlertRecipientsTable;
